'use client'

import Script from 'next/script'
import { useDeferredTag } from '@/hooks/useDeferredTag'
import { CLARITY_PROJECT_ID } from '@/lib/tracking/config'
import type { DeferredTagSpec } from '@/lib/tracking/deferred-tags'

/**
 * Microsoft Clarity — session replay + heatmaps.
 *
 * Two-stage load: the `clarity` queue stub (the first half of Microsoft's
 * snippet) mounts afterInteractive so any `window.clarity(...)` call queues
 * instead of no-op'ing — today no app code calls it — and the tag itself is
 * injected by `useDeferredTag` on the first user interaction or
 * CLARITY_FALLBACK_MS after `load`, then held CLARITY_SETTLE_MS so it never
 * evaluates back to back with fbevents.js on the same tap. The old `lazyOnload`
 * (= load + requestIdleCallback) still landed inside the Lighthouse trace
 * window and measured 336 ms of blocking time on the exam PDP; the recorder is
 * the documented first thing to demote ("if INP tightens, demote Clarity, not
 * the pixel" — META_TRACKING_PLAN.md §6).
 *
 * No first-party code depends on Clarity: nothing reads its cookies, nothing
 * calls it. What changes for the data: a recording starts at the visitor's
 * first interaction instead of a beat after load, and a visitor who leaves
 * before interacting and before the fallback produces no Clarity session (it
 * was an empty recording before).
 *
 * The project id lives in tracking config alongside the Google tag ids, so
 * every analytics id is configured in one place.
 */

const CLARITY_FALLBACK_MS = 8000
const CLARITY_SETTLE_MS = 1500

const CLARITY: DeferredTagSpec = {
  id: 'clarity',
  src: `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`,
  fallbackMs: CLARITY_FALLBACK_MS,
  settleMs: CLARITY_SETTLE_MS,
}

export function ClarityAnalytics() {
  useDeferredTag(CLARITY, Boolean(CLARITY_PROJECT_ID))

  if (!CLARITY_PROJECT_ID) return null

  return (
    <Script id="ms-clarity" strategy="afterInteractive">
      {`(function(c,a){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)}})(window,"clarity")`}
    </Script>
  )
}
